/**
 * Grid query-param builders for odl_app list endpoints.
 *
 * One home for turning grid state into the wire query params the server
 * expects: sort (comma-list, - prefix for desc), filter[field] /
 * filter[field.op] operator pairs, filter[search], and limit / offset
 * pagination.
 *
 * Contains:
 * - buildGridParams — { sort, filters, search, page } descriptor -> params
 * - buildGridListParams — grid sort/quick-filter state -> DRF-style list params
 * - toJsonApiParams — useServerDataGrid DRF-style params -> JSON:API format
 */

/**
 * Builds the query-string params object for a paginated, filterable grid
 * request against any odl_app list endpoint.
 *
 * @param {object} [opts]
 * @param {string|string[]} [opts.sort]    Column sort: "-created" or ["-created","name"]
 * @param {object}          [opts.filters] Field → value or { op, value } pairs
 * @param {string}          [opts.search]  Full-text search term (filter[search])
 * @param {{ limit: number, offset: number }} [opts.page] Pagination window
 * @returns {object} Flat params object ready for axios `params`
 */
export function buildGridParams({ sort, filters = {}, search, page } = {}) {
  const params = {};

  if (sort) {
    params.sort = Array.isArray(sort) ? sort.join(',') : sort;
  }

  if (search) {
    params['filter[search]'] = search;
  }

  for (const [field, val] of Object.entries(filters)) {
    if (val && typeof val === 'object' && 'op' in val) {
      // Operator pair: { op: 'gte', value: '2026-01-01' }
      params[`filter[${field}.${val.op}]`] = val.value;
    } else {
      // Simple equality filter
      params[`filter[${field}]`] = val;
    }
  }

  if (page) {
    params.limit = page.limit;
    params.offset = page.offset;
  }

  return params;
}

/**
 * Map a grid's sort model and quick-filter text onto the DRF-style list params
 * a grid request carries, minus the pagination window.
 *
 * The grid's on-screen fetch and its CSV export must ask the server the same
 * question — same filters, same search, same order — or the exported file is
 * not the list the user is looking at. Both read their params from here so the
 * two cannot drift; only the limit/offset window differs, and each caller owns
 * that.
 *
 * @param {object} [opts]
 * @param {object} [opts.extraParams]  Caller-owned filter values, copied into
 *   the result untouched (the serializer turns them into `filter[...]`).
 * @param {Array<{ field: string, sort: string }>} [opts.sortModel]  DataGrid sort model.
 * @param {string[]} [opts.quickFilterValues]  DataGrid quick-filter terms.
 * @param {string[]} [opts.searchFields]  Backend field names the search is
 *   restricted to; empty means whole-row search.
 * @param {?string} [opts.tiebreakerField='id']  Final sort key appended so
 *   LIMIT/OFFSET paging stays deterministic when the primary sort has ties.
 *   Falsy sends none, for endpoints that cannot order by it.
 * @returns {object} `{ ...extraParams, o?, text_search? }`
 */
export function buildGridListParams({
  extraParams = {},
  sortModel = [],
  quickFilterValues = [],
  searchFields = [],
  tiebreakerField = 'id',
} = {}) {
  const params = { ...extraParams };

  // The tiebreaker rides behind a sort the reader chose; it is not a sort of
  // its own. Sending it alone would be a request that names an order, which
  // is what the grid's own default order answers — so an unsorted grid sends
  // no order at all and opens the way its descriptor says it opens, rather
  // than by id.
  const sortFields = sortModel.map(({ field, sort }) => (sort === 'desc' ? `-${field}` : field));
  if (sortFields.length > 0) {
    if (tiebreakerField && !sortFields.some(f => f === tiebreakerField || f === `-${tiebreakerField}`)) {
      sortFields.push(tiebreakerField);
    }
    params.o = sortFields.join(',');
  }

  const quickText = quickFilterValues.join(' ');
  if (quickText) {
    const formatted = quickText.split(' ').join(',');
    params.text_search = searchFields.length === 0 ? formatted : `${searchFields.join(',')}|${formatted}`;
  }

  return params;
}

/**
 * Translate useServerDataGrid wire params (DRF-style) into JSON:API format.
 *
 * The hook sends { o, offset, limit, text_search?, ...extraParams } where
 * extraParams keys are plain field names. The backend expects { sort, offset,
 * limit, 'filter[<field>]': value } — axios serialises bracket keys correctly.
 * `text_search` maps to the reserved `filter[search]` key (odl-app grids define
 * which fields it searches via `searchable` in their GridSpec).
 *
 * @param {Object} hookParams
 * @returns {Object}
 */
export function toJsonApiParams(hookParams) {
  const { o, offset, limit, text_search: textSearch, ...rest } = hookParams;
  const wire = {};
  if (o) wire.sort = o;
  if (offset != null) wire.offset = offset;
  if (limit != null) wire.limit = limit;
  if (textSearch) wire['filter[search]'] = textSearch;
  for (const [key, value] of Object.entries(rest)) {
    if (value === '' || value == null) continue;
    wire[`filter[${key}]`] = value;
  }
  return wire;
}
